import { gql } from 'apollo-angular';

export const SAVE_COMPANY_INFORMATION = gql`
  mutation SaveCompanyInformation($input: CompanyInformationDtoInput!) {
    saveCompanyInformation(input: $input) {
      data {
        countryUuid
        active
        addressDescription
        brelaDataFetched
        brelaFetchedAt
        brelaRegistrationNumber
        companyType
        contactDetails {
          active
          contactType
          content
          uuid
        }
        directors {
          active
          appointmentDate
          email
          identificationNumber
          name
          nationality
          phone
          title
          uuid
        }
        name
        postalCode
        registrationStatus
        shareholders {
          active
          contactEmail
          contactPhone
          id
          identificationNumber
          name
          nationality
          ownershipPercentage
          uuid
        }
        taxpayerNumber
        tin
        vrn
        uuid
        website
      }
      code
      extras
      fieldsErrors
      message
      status
    }
  }
`;

export const SAVE_COMPANY_TAX_INFORMATION = gql`
  mutation SaveTaxInformation($input: TaxInformationDtoInput!) {
    saveTaxInformation(input: $input) {
      code
      extras
      fieldsErrors
      status
      message
      data {
        brelaRegistrationNumber
        companyType
        countryUuid
        taxpayerNumber
        tin
        vrn
        uuid
      }
    }
  }
`;

export const SAVE_SHAREHOLDERS_INFORMATION = gql`
  mutation UpdateShareholders($shareholders: [ShareholderItemInput], $companyUuid: String!) {
    updateShareholders(shareholders: $shareholders, companyUuid: $companyUuid) {
      code
      extras
      fieldsErrors
      message
      status
      data {
        active
        addressDescription
        brelaDataFetched
        brelaFetchedAt
        brelaRegistrationNumber
        companyType
        countryUuid
        id
        name
        contactDetails {
          uuid
          content
          contactType
        }
        postalCode
        registrationCompletedAt
        registrationStatus
        taxpayerNumber
        tin
        vrn
        uuid
        website
      }
    }
  }
`;

export const SUBMIT_COMPANY_REGISTRATION = gql`
  mutation SubmitCompanyRegistration($companyUuid: String) {
    submitCompanyRegistration(companyUuid: $companyUuid) {
      code
      extras
      fieldsErrors
      message
      status
      data {
        active
        addressDescription
        brelaDataFetched
        brelaFetchedAt
        brelaRegistrationNumber
        companyType
        countryUuid
        id
        name
        postalCode
        registrationCompletedAt
        registrationStatus
        taxpayerNumber
        tin
        vrn
        uuid
        website
        contactDetails {
          active
          contactType
          content
          id
          uuid
        }
      }
    }
  }
`;

export const SAVE_DIRECTOS_INFORMATION = gql`
  mutation UpdateDirectors($directors: [DirectorItemInput], $companyUuid: String!) {
    updateDirectors(directors: $directors, companyUuid: $companyUuid) {
      code
      extras
      fieldsErrors
      message
      status
      data {
        active
        addressDescription
        brelaDataFetched
        brelaFetchedAt
        brelaRegistrationNumber
        companyType
        countryUuid
        id
        name
        contactDetails {
          uuid
          content
          contactType
        }
        postalCode
        registrationCompletedAt
        registrationStatus
        taxpayerNumber
        tin
        vrn
        uuid
        website
      }
    }
  }
`;

export const GET_REGISTRATION_PROGRESS = gql`
  query GetCompanyRegistrationProgress($companyUuid: String!) {
    getCompanyRegistrationProgress(companyUuid: $companyUuid) {
      data {
        allSectionsComplete
        company {
          uuid
        }
        completionPercentage
        sectionAComplete
        sectionACompletedAt
        sectionBComplete
        sectionBCompletedAt
        sectionCComplete
        sectionCCompletedAt
        sectionDComplete
        sectionDCompletedAt
        sectionEComplete
        sectionECompletedAt
      }
      status
      message
      fieldsErrors
      extras
    }
  }
`;

export const GET_COMPANY_INFORMATION = gql`
  query GetMyCompany(
    $includeStakeholders: Boolean = false
    $brelaVerificationRequest: Boolean = false
    $attachmentsIncluded: Boolean = false
    $contactDetailsIncluded: Boolean = false
    $includeRegistrationProgress: Boolean = false
    $includeOMC: Boolean = false
    $includeTerminal: Boolean = false
    $includeSupplier: Boolean = false
    $includeNonBpsOperator: Boolean = false
    $includeTaxClearanceCertificates: Boolean = false
    $includeAgent: Boolean = false
  ) {
    getMyCompany {
      data {
        name
        postalCode
        registrationStatus
        countryUuid
        active
        addressDescription
        brelaDataFetched
        brelaFetchedAt
        brelaAnnualReturnDate
        brelaAnnualReturnYear
        brelaRegistrationNumber
        companyType
        taxpayerNumber
        tin
        vrn
        uuid
        website
        taxIncorporationNumber
        taxPayerCenter
        taxPayerName
        taxPayerRegion
        taxPayerRegistrationDate
        hasActiveClearanceCertificate
        brelaVerificationRequest @include(if: $brelaVerificationRequest) {
          active
          id
          status
          uuid
          verifiedBrelaRegistrationNumber
          bills {
            billDate
            billNumber
            billStatus
            clientName
            clientPhoneNumber
            controlNumber
            currency
            description
            id
            totalAmountAfterTax
            uuid
            receipts {
              amount
              currency
              paymentDate
              receiptNo
              transactionId
              uuid
            }
            itemList {
              collectionCenter
              collectionCenterId
              description
              gfsCode
              id
              isExempted
              quantity
              taxAmount
              totalAmount
              unitPrice
            }
          }
        }
        contactDetails @include(if: $contactDetailsIncluded) {
          active
          contactType
          content
          uuid
        }
        attachments @include(if: $attachmentsIncluded) {
          active
          id
          minioObjectName
          uuid
          attachable {
            id
          }
          attachmentType {
            active
            fileType
            id
            name
            uuid
          }
        }
        directors @include(if: $includeStakeholders) {
          active
          appointmentDate
          email
          identificationNumber
          name
          nationality
          phone
          title
          uuid
        }
        shareholders @include(if: $includeStakeholders) {
          active
          contactEmail
          contactPhone
          id
          identificationNumber
          name
          nationality
          ownershipPercentage
          uuid
        }
        registrationProgress @include(if: $includeRegistrationProgress) {
          allSectionsComplete
          completionPercentage
          sectionAComplete
          sectionACompletedAt
          sectionBComplete
          sectionBCompletedAt
          sectionCComplete
          sectionCCompletedAt
          sectionDComplete
          sectionDCompletedAt
          sectionEComplete
          sectionECompletedAt
        }
        getOmcRegistration @include(if: $includeOMC) {
          active
          address
          attachableDiscriminator
          brelaExpireDate
          brelaLicenseNumber
          brelaRegDate
          companyName
          email
          ewuraExpireDate
          ewuraLicenseNumber
          ewuraRegDate
          name
          registrationNumber
          registrationDate
          otherAttachmentName
          phoneNumber
          registrationStatus
          registrationType
          tin
          type
          uuid
        }
        getTerminalRegistration @include(if: $includeTerminal) {
          active
          address
          attachableDiscriminator
          bucketName
          ewuraExpireDate
          ewuraLicenseNumber
          ewuraRegDate
          lgaExpireDate
          lgaLicenseNumber
          lgaRegDate
          otherAttachmentName
          referenceNumber
          regionDistrict
          registrationType
          status
          terminalName
          uuid
        }
        getSupplier @include(if: $includeSupplier) {
          active
          id
          name
          uuid
        }
        nonBpsOperator @include(if: $includeNonBpsOperator) {
          active
          businessScopeDescription
          id
          importationLicenseExpiration
          registrationStatus
          registrationType
          tin
          traImportationLicense
          uuid
          registrationStatus
          natureOfNonBps {
            active
            code
            name
            uuid
          }
        }
        getAgentRegistration @include(if: $includeAgent) {
          active
          name
          uuid
          registrationStatus
        }
        taxClearanceCertificates @include(if: $includeTaxClearanceCertificates) {
          businessActivities {
              actBranch
              actClass
              actDiv
              actGroup
              actSect
              active
              busAct
              id
              uuid
          }
          active
          expiryDate
          id
          issueDate
          issueOfficeName
          taxCertificateNumber
          uuid
          validityStatus
        }
      }
      code
      extras
      fieldsErrors
      message
      status
    }
  }
`;

export const VERIFY_BRELA_REGISTRATION = gql`
  query VerifyBrelaRegistration($brelaNumber: String!, $confirmUsingDifferentNumber: Boolean!) {
    verifyBrelaRegistration(
      brelaRegistrationNumber: $brelaNumber
      confirmUsingDifferentNumber: $confirmUsingDifferentNumber
    ) {
      data {
        brelaRegistrationNumber
        name: companyName
        fetchedAt
      }
      code
      extras
      fieldsErrors
      message
      status
    }
  }
`;

export const VERIFY_TIN_REGISTRATION = gql`
  query VerifyTaxId($input: TaxIdVerificationDtoInput) {
    verifyTaxId(input: $input) {
      code
      extras
      fieldsErrors
      message
      status
      data {
        taxCenter
        taxRegion
        taxpayerName
        tin
        vrn
        verificationStatus
         taxClearance {
                bizClsfCd
                description
                docNm
                hasActiveCertificate
                intfId
                licensingAuthTin
                parnSysId
                status
                statusCode
                taxpayerTin
            }
      }
    }
  }
`;

export const ATTACH_DOCUMENTS = gql`
  mutation UploadAttachment($input: AttachmentUploadDtoInput!) {
    uploadAttachment(attachmentDto: $input) {
      code
      data {
        active
        id
        minioObjectName
        uuid
        attachable {
          attachableDiscriminator
          bucketName
          id
          registrationType
          uuid
        }
        attachmentType {
          active
          fileType
          id
          name
          uuid
        }
      }
      extras
      fieldsErrors
      message
      status
    }
  }
`;

export const GET_FEE_CONFIGURATION_BY_FEE_TYPE = gql`
  query GetFeeConfigurationByType($feeType: FeeType!) {
    getFeeConfigurationByType(feeType: $feeType) {
      active
      amount
      amountUsd
      feeCode
      feeType
      gfsCode
      id
      name
      taxInclusive
      uuid
    }
  }
`;

export const BRELA_BILL_CONFIRMATION = gql`
  mutation BrelaBillConfirmation($companyUuid: String!) {
    brelaBillConfirmation(companyUuid: $companyUuid) {
      code
      data {
        active
        id
        status
        updatedAt
        updatedBy
        uuid
        verifiedBrelaRegistrationNumber
        bills {
          billDate
          billNumber
          billStatus
          clientName
          clientPhoneNumber
          controlNumber
          currency
          description
          id
          totalAmountAfterTax
          uuid
          itemList {
            collectionCenter
            collectionCenterId
            description
            gfsCode
            id
            isExempted
            quantity
            taxAmount
            totalAmount
            unitPrice
          }
        }
      }
      fieldsErrors
      extras
      message
      status
    }
  }
`;

export const UPDATE_CONTACT_DETAILS = gql`
  mutation UpdateContactDetails($contactDetails: ContactDetailsDtoInput, $contactUuid: String) {
    updateContactDetails(contactDetails: $contactDetails, contactUuid: $contactUuid) {
      code
      extras
      fieldsErrors
      message
      status
      data {
        active
        contactDetails {
          active
          content
          contactType
          uuid
          id
        }
      }
    }
  }
`;

export const UPDATE_COMPANY_ADDRESS_DESCRIPTION = gql`
  mutation UpdateCompanyAddressDescription($addressDescription: String) {
    updateCompanyAddressDescription(addressDescription: $addressDescription) {
      code
      data {
        active
        addressDescription
        uuid
        id
      }
      extras
      fieldsErrors
      message
      status
    }
  }
`;
